
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, CheckCircle, XCircle, Copy, Trash2, Eye } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { adminApi } from "@/lib/adminApi";
import { cn, publicProposalUrl } from "@/lib/utils";

interface ProposalDetail {
  id: string;
  title: string;
  proposeeName: string;
  proposeeEmail: string | null;
  proposeePhone: string | null;
  slug: string;
  proposalType: string;
  status: string;
  subtitle: string | null;
  askText: string | null;
  musicType: string | null;
  musicUrl: string | null;
  thumbnailUrl: string | null;
  sentAt: string | null;
  rejectionReason: string | null;
  appealStatus: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    name: string | null;
    email: string;
  };
  slides: Array<{
    id: string;
    order: number;
    type: string;
    contentText: string | null;
    imageUrl: string | null;
    effectEntry: string;
    effectAmbient: string;
    effectIntensity: number;
    duration: number;
    enableAutoAdvance: boolean;
  }>;
  viewEvents: Array<{
    id: string;
    slideIndex: number;
    duration: number;
    viewedAt: string;
  }>;
}

const statusColors: Record<string, string> = {
  DRAFT: "bg-zinc-100",
  PUBLISHED: "bg-emerald-100",
  SENT: "bg-blue-100 dark:bg-blue-900/50",
  ACCEPTED: "bg-purple-100",
  DECLINED: "bg-red-100 dark:bg-red-900/50",
};

const slideTypeIcons: Record<string, string> = {
  TITLE: "📝",
  MESSAGE: "💬",
  PHOTO: "📷",
  MEMORY: "💭",
  QUESTION: "❓",
  FINAL: "💍",
};

export default function ProposalDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState<ProposalDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    async function fetchProposal() {
      try {
        const res = await adminApi.get<{ success: boolean; proposal: ProposalDetail }>(
          `/admin/proposals/${params.id}`
        );
        if (res.success) {
          setProposal(res.proposal);
        }
      } catch (e: any) {
        setError(e.message || "Failed to load proposal");
      } finally {
        setLoading(false);
      }
    }
    fetchProposal();
  }, [params.id]);

  const handleUpdateStatus = async (newStatus: string) => {
    if (!proposal) return;
    setActionLoading(true);
    try {
      await adminApi.put(`/admin/proposals/${proposal.id}`, { status: newStatus });
      setProposal({ ...proposal, status: newStatus });
    } catch (e) {
      console.error("Failed to update status", e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSend = async () => {
    if (!proposal) return;
    setActionLoading(true);
    try {
      await adminApi.put(`/admin/proposals/${proposal.id}/send`, {});
      setProposal({ ...proposal, status: "SENT", sentAt: new Date().toISOString() });
    } catch (e) {
      console.error("Failed to send", e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!proposal) return;
    if (!confirm("Delete this proposal? This cannot be undone.")) return;
    setActionLoading(true);
    try {
      await adminApi.delete(`/admin/proposals/${proposal.id}`);
      navigate("/proposals");
    } catch (e) {
      console.error("Failed to delete", e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!proposal) return;
    navigator.clipboard.writeText(publicProposalUrl(proposal.slug));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold">Loading...</h1>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-64 rounded-lg" />
          <div className="h-32 rounded-lg" />
        </div>
      </div>
    );
  }

  if (error || !proposal) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold">Error</h1>
        </div>
        <p className="text-red-500">{error || "Proposal not found"}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">{proposal.title}</h1>
            <p className="text-sm">For: {proposal.proposeeName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={cn("inline-flex rounded-full px-3 py-1 text-sm font-medium", statusColors[proposal.status])}>
            {proposal.status}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Proposal Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm">Type</p>
                  <p className="font-medium">{proposal.proposalType}</p>
                </div>
                <div>
                  <p className="text-sm">Slug</p>
                  <p className="font-mono text-sm">{proposal.slug}</p>
                </div>
                <div>
                  <p className="text-sm">Creator</p>
                  <p className="font-medium">{proposal.user.name || proposal.user.email}</p>
                </div>
                <div>
                  <p className="text-sm">Sent At</p>
                  <p className="font-medium">
                    {proposal.sentAt ? new Date(proposal.sentAt).toLocaleString() : "Not sent"}
                  </p>
                </div>
                {proposal.rejectionReason && (
                  <div className="col-span-2">
                    <p className="text-sm">Rejection Reason</p>
                    <p className="font-medium text-red-500">{proposal.rejectionReason}</p>
                  </div>
                )}
                {proposal.appealStatus !== "none" && (
                  <div className="col-span-2">
                    <p className="text-sm">Appeal Status</p>
                    <p className="font-medium capitalize">{proposal.appealStatus}</p>
                  </div>
                )}
              </div>
              {proposal.subtitle && (
                <div>
                  <p className="text-sm">Subtitle</p>
                  <p>{proposal.subtitle}</p>
                </div>
              )}
              {proposal.askText && (
                <div>
                  <p className="text-sm">Ask Text</p>
                  <p className="font-medium">{proposal.askText}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Slides ({proposal.slides.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {proposal.slides.map((slide) => (
                  <div
                    key={slide.id}
                    className="flex items-center gap-4 p-3/50 rounded-lg"
                  >
                    <span className="text-2xl">{slideTypeIcons[slide.type] || "📄"}</span>
                    <div className="flex-1">
                      <p className="font-medium">{slide.type}</p>
                      {slide.contentText && (
                        <p className="text-sm truncate">{slide.contentText}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xs">Order: {slide.order}</p>
                      <p className="text-xs">{slide.duration}s</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>View Events ({proposal.viewEvents.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {proposal.viewEvents.length === 0 ? (
                <p className="text-zinc-500">No views yet</p>
              ) : (
                <div className="space-y-2">
                  {proposal.viewEvents.slice(0, 10).map((event) => (
                    <div
                      key={event.id}
                      className="flex items-center justify-between p-2/50 rounded"
                    >
                      <span className="text-sm">Slide {event.slideIndex + 1}</span>
                      <span className="text-sm">{event.duration}s</span>
                      <span className="text-xs">
                        {new Date(event.viewedAt).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {proposal.status === "PUBLISHED" && (
                <button
                  onClick={handleSend}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                  Send Proposal
                </button>
              )}
              {(proposal.status === "SENT" || proposal.status === "PUBLISHED") && (
                <>
                  <button
                    onClick={() => handleUpdateStatus("ACCEPTED")}
                    disabled={actionLoading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Accept
                  </button>
                  <button
                    onClick={() => handleUpdateStatus("DECLINED")}
                    disabled={actionLoading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    <XCircle className="h-4 w-4" />
                    Decline
                  </button>
                </>
              )}
              <button
                onClick={handleCopyLink}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg hover:bg-zinc-200"
              >
                <Copy className="h-4 w-4" />
                Copy Link
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 dark:bg-red-900/30 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                Delete Proposal
              </button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Proposal Link</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm mb-2">Public URL:</p>
              <p className="font-mono text-xs break-all">
                {publicProposalUrl(proposal.slug)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Metadata</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-500">Created</span>
                <span>{new Date(proposal.createdAt).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Updated</span>
                <span>{new Date(proposal.updatedAt).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">ID</span>
                <span className="font-mono text-xs">{proposal.id}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}